import { useCallback, useEffect, useState } from 'react';
import { Badge, Skeleton } from '../components/primitives';
import { ConfirmModal } from '../components/Modal';
import type { Role, UserItem } from '../types';
import { useAdmin } from './context';

const ROLES: Role[] = ['viewer', 'admin', 'master'];
const ROLE_HELP: Record<Role, string> = {
  viewer: 'Read-only: sees all forms, can open their public pages',
  admin: 'Can create, edit, publish and archive forms',
  master: 'Admin + this user management and the audit log',
};

const inputCls =
  'w-full rounded-xl border border-border bg-input px-3.5 py-2.5 text-sm outline-none transition-colors placeholder:text-muted/70 focus:border-brand';

export function Users() {
  const { api, me } = useAdmin();
  const [users, setUsers] = useState<UserItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('admin');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<UserItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingRole, setPendingRole] = useState<{ user: UserItem; role: Role } | null>(null);

  const flash = (msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice(null), 8000);
  };

  const load = useCallback(async () => {
    try {
      const { users } = await api.listUsers();
      setUsers(users);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load users');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    setAdding(true);
    setError(null);
    try {
      const { user } = await api.addUser({ email: email.trim().toLowerCase(), ...(name.trim() ? { name: name.trim() } : {}), role });
      flash(`Added ${user.email} as ${user.role} — they'll keep that role from their first sign-in.`);
      setEmail('');
      setName('');
      setRole('admin');
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add user');
    } finally {
      setAdding(false);
    }
  };

  const changeRole = async () => {
    if (!pendingRole) return;
    setBusy(true);
    try {
      await api.setUserRole(pendingRole.user.email, pendingRole.role);
      flash(`${pendingRole.user.email} is now ${pendingRole.role}.`);
      setPendingRole(null);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Role change failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await api.removeUser(removing.email);
      flash(`Removed ${removing.email}. If they sign in again, they'll start over as a read-only viewer.`);
      setRemoving(null);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold">Users</h1>
      <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted">
        Every knocking.com Workspace account can sign in and lands as a read-only <em>viewer</em>. Upgrade
        people here when they should manage forms.
      </p>

      {notice && (
        <p role="status" className="mt-4 rounded-xl border border-brand/40 bg-brand/10 px-4 py-2.5 text-sm">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-4 rounded-xl border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm">
          {error}
        </p>
      )}

      <form
        className="mt-6 flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <div className="flex-1">
          <label htmlFor="u-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted">
            Add user (knocking.com email)
          </label>
          <input
            id="u-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="firstname.lastname@knocking.com"
            inputMode="email"
            autoComplete="off"
            className={inputCls}
            required
          />
        </div>
        <div className="sm:w-44">
          <label htmlFor="u-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted">
            Name <span className="font-normal normal-case">(optional)</span>
          </label>
          <input id="u-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Firstname Lastname" className={inputCls} />
        </div>
        <div className="sm:w-40">
          <label htmlFor="u-role" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted">
            Role
          </label>
          <select id="u-role" value={role} onChange={(e) => setRole(e.target.value as Role)} className={inputCls}>
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={adding || !/^[^\s@]+@knocking\.com$/.test(email.trim().toLowerCase())}
          className="rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-brand-contrast transition-colors hover:bg-brand-strong disabled:opacity-50"
        >
          {adding ? 'Adding…' : 'Add user'}
        </button>
      </form>

      {!users && !error && (
        <div className="mt-6 flex flex-col gap-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {users && (
        <ul className="mt-6 flex flex-col gap-3">
          {users.map((user) => {
            const isSelf = user.email === me.email;
            return (
              <li
                key={user.email}
                className="flex flex-col gap-3 rounded-2xl border border-border bg-card px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-semibold">{user.name ?? user.email}</span>
                    <Badge tone={user.role === 'master' ? 'brand' : user.role === 'admin' ? 'ok' : 'muted'}>
                      {user.role}
                    </Badge>
                    {isSelf && <Badge tone="muted">you</Badge>}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted">
                    {user.email}
                    {' · '}added {new Date(user.createdAt).toLocaleDateString()}
                    {user.lastSeenAt && ` · last seen ${new Date(user.lastSeenAt).toLocaleDateString()}`}
                  </p>
                  <p className="mt-1 text-xs text-muted">{ROLE_HELP[user.role]}</p>
                </div>
                {!isSelf && (
                  <div className="flex shrink-0 items-center gap-2">
                    <select
                      aria-label={`Role for ${user.email}`}
                      value={user.role}
                      onChange={(e) => setPendingRole({ user, role: e.target.value as Role })}
                      className="rounded-xl border border-border bg-input px-3 py-2 text-sm outline-none focus:border-brand"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setRemoving(user)}
                      aria-label={`Remove ${user.email}`}
                      title="Remove user"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-danger/40 text-danger transition-colors hover:bg-danger/10"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      </svg>
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pendingRole && (
        <ConfirmModal
          title={`Change ${pendingRole.user.email} to ${pendingRole.role}?`}
          message={ROLE_HELP[pendingRole.role]}
          confirmLabel="Change role"
          busy={busy}
          onConfirm={() => void changeRole()}
          onClose={() => setPendingRole(null)}
        />
      )}

      {removing && (
        <ConfirmModal
          title={`Remove ${removing.email}?`}
          message="Their user record is deleted. If they sign in again later, they start over as a read-only viewer. The audit log keeps the history."
          confirmLabel="Remove user"
          danger
          busy={busy}
          onConfirm={() => void remove()}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
